import { type RewardedVideoPayload, type VideoFormState, VideoOption } from '@openx/types/targeting/video';
import { noop } from '@openx/utils/noop';
import { type JSX, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionChipLine } from '../../../shared/DimensionChip/DimensionChipLine';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import { SelectedItems } from '../../../shared/SelectedItems/SelectedItems';
import { getRewardedVideoOptions } from '../../utils/utils';

type PrevStateCallback = (prevState: VideoFormState) => VideoFormState;

type RewardedVideoItemProps = {
  readonly?: boolean;
  params: RewardedVideoPayload;
  shouldAddDimensionLine: boolean;
  onChange?: (newData: VideoFormState | PrevStateCallback) => void;
  disabledReasonMessage?: string;
};

export const RewardedVideoItem = ({
  params,
  readonly = true,
  shouldAddDimensionLine,
  onChange = noop,
  disabledReasonMessage,
}: RewardedVideoItemProps): JSX.Element => {
  const { t } = useTranslation();

  const videoOptions = useMemo(() => getRewardedVideoOptions(t), [t]);
  const { op } = params;

  const handleChange = useCallback(() => {
    onChange(state => ({
      ...state,
      [VideoOption.REWARDED_VIDEO]: null,
    }));
  }, [onChange]);

  return (
    <>
      <FiltersTitle
        onClear={handleChange}
        title={t('Rewarded Video')}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />
      <SelectedItems
        isAddIcon
        selectedItems={[op]}
        readonly={readonly}
        handleChange={handleChange}
        options={videoOptions}
        dataTest="rewarded-video"
      />
      {shouldAddDimensionLine && <DimensionChipLine />}
    </>
  );
};
